import { useState, useMemo, useId } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { DesignedScrollArea } from '@/react/components/ui/designed-scroll-area';
import { AlertIcon } from '../icons';
import { useUpdateOrderStatus } from '@hooks/orders';
import { useOrderEvents } from '@hooks/events';
import { SpineCancellationNotice } from './SpineCancellationNotice';
import { spineCancellationTargets } from './spine-cancellation';
import { useTranslation } from '@hooks/useTranslation';
import { useToast } from '@/hooks/use-toast';
import { usePostHog } from '@posthog/react';
import { getAllErrorReasons } from '@api/client';

interface CancelOrderModalProps {
  orderId: number;
  orderNumber?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Mirrors Attio's "Closed lost reason(s)" options, in its order, so a rep picks
// the same words in either system. insufficient_lead_time and
// out_of_service_band are portal-only — Attio has no equivalent and they sync
// across as Others.
const CANCELLATION_REASON_KEYS = [
  'no_response',
  'event_cancelled',
  'budget_constraint',
  'event_budget_too_high',
  'not_enough_variety',
  'not_value_for_money',
  'full_capacity_ops',
  'full_capacity_kitchen',
  'closed_on_phs',
  'closed_on_sundays',
  'want_sit_down_event',
  'requires_alcohol',
  'israel_iran_conflict',
  'insufficient_lead_time',
  'out_of_service_band',
  'other',
] as const;

export function CancelOrderModal({ orderId, orderNumber, open, onOpenChange }: CancelOrderModalProps) {
  const posthog = usePostHog();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [selectedReason, setSelectedReason] = useState<string>('no_response');
  const { mutate: updateOrderStatus, isPending } = useUpdateOrderStatus();
  // Cancelling stays enabled even when the events can't be read — an outage
  // here must not stop anyone cancelling an order.
  const { data: events, isPending: eventsPending, isError: eventsFailed } = useOrderEvents(orderId);
  const spineTargets = useMemo(() => spineCancellationTargets(events ?? []), [events]);
  const spineUnavailable = eventsPending || eventsFailed;
  // Describes the confirm button, so the caveat is read out on focus.
  const spineNoticeId = useId();

  const cancellationReasons = useMemo(
    () => CANCELLATION_REASON_KEYS.map((key) => ({ value: key, label: t(`cancel_modal.reasons.${key}`) })),
    [t],
  );

  const handleConfirmCancellation = () => {
    updateOrderStatus(
      { id: orderId, status: 'cancelled', cancellation_reason: selectedReason },
      {
        onSuccess: () => {
          posthog.capture('order_cancelled', {
            order_id: orderId,
            cancellation_reason: selectedReason,
          });
          onOpenChange(false);
          const label = orderNumber ? `${t('cancel_modal.success_title')}: ${orderNumber}` : t('cancel_modal.success_title');
          toast({ description: label, variant: 'destructive' });
        },
        onError: (error: Error) => {
          onOpenChange(false);
          toast({
            description: getAllErrorReasons(error, t('toast.status_update_failed')),
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 max-w-md rounded-input bg-body border-dinnerplate-white border [&>button]:hidden">
        <div className="flex flex-col gap-3 px-6 py-6 border-b border-dinnerplate-white">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-chilli-blush shrink-0">
                <AlertIcon className="size-6 text-chilli-red" />
              </div>
              <div className="flex flex-col gap-1.5">
                <DialogTitle className="font-inter text-lg font-semibold leading-normal text-off-black text-left">
                  {t('cancel_modal.title')}
                </DialogTitle>
                <DialogDescription className="font-inter text-sm font-normal leading-tight text-off-black text-left">
                  {t('cancel_modal.description')}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Attio's reason list is long enough to push the buttons off a laptop screen. */}
        <DesignedScrollArea
          className="h-72"
          contentBg="bg-body"
          vLaneBorder
        >
          <RadioGroup value={selectedReason} onValueChange={setSelectedReason} className="flex flex-col gap-6 p-6">
            {cancellationReasons.map((reason) => (
              <label
                key={reason.value}
                className="flex items-center gap-3 cursor-pointer"
                htmlFor={`reason-${reason.value}`}
              >
                <RadioGroupItem value={reason.value} id={`reason-${reason.value}`} />
                <span className="font-inter text-sm font-normal leading-tight text-off-black">
                  {reason.label}
                </span>
              </label>
            ))}
          </RadioGroup>
        </DesignedScrollArea>

        <SpineCancellationNotice
          id={spineNoticeId}
          targets={spineTargets}
          unavailable={spineUnavailable}
          className="mx-6 mb-6"
        />

        <DialogFooter className="flex flex-row items-center gap-3 px-6 py-6 border-t border-dinnerplate-white">
          <Button            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            size="dialog-tight"
            className="border border-dinnerplate-white font-semibold text-off-black hover:bg-transparent"
          >
            {t('cancel_modal.stay_on_page')}
          </Button>
          <Button
            variant="destructive-subtle"
            size="dialog-tight"
            onClick={handleConfirmCancellation}
            disabled={isPending}
            aria-describedby={spineNoticeId}
          >
            {t('cancel_modal.confirm_cancellation')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
